/**
 * 横断 PMO（pi_div 以外 × portalRole='pmo'）向け /projects 画面のフロントエンド SSOT。
 *
 * - business 切替タブの選択肢
 * - チーム filter のホワイトリスト + 配色
 * - 画面右上に表示する Div バッジ色（core-types の `CROSS_DIV_PMO_SCOPE.badgeColor` を踏襲）
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 横断 PMO 向け /projects UI 用 SSOT（business 切替候補・チーム配色・Div バッジ色）
 * @graph-connects none
 */
import type { CrossDivPmoDivision } from '@cortex/core-types';

/** チームコードの背景色（淡色）/ 文字色 ペア。 */
export interface TeamChipColor {
  readonly bg: string;
  readonly text: string;
}

/** 横断 PMO Div ごとの UI メタデータ。 */
export interface CrossDivPmoUiConfig {
  /** Div バッジに表示する短いラベル（i18n 不要、英字固定）。 */
  readonly divLabel: string;
  /** Div バッジ背景色（HEX）。 */
  readonly divBadgeColor: string;
  /**
   * business 切替タブで表示する選択肢。null の場合は business 切替を出さず、
   * 単一 business 固定として扱う（Mall / Log は business 切替なし）。
   */
  readonly businessTabs: readonly string[] | null;
  /** チーム filter のホワイトリスト（pb_sheet_sync.responsible_team の値）。 */
  readonly teams: readonly string[];
  /** チーム → 色マッピング。 */
  readonly teamColors: Readonly<Record<string, TeamChipColor>>;
}

/** @graph-connects none */
export const CROSS_DIV_PMO_UI: Readonly<Record<CrossDivPmoDivision, CrossDivPmoUiConfig>> = {
  ac_div: {
    divLabel: 'aC Div',
    divBadgeColor: '#DC4040',
    businessTabs: ['エアクロ', 'Spot Rental', 'エアクロメンズ'],
    // 2026-08 体制変更後の aC Div 3 グループ配下の全チーム（GRG: NBT/CDT、CMG: BDT/FCT、
    // CXG: QDT/CLT/STYT）。ここは `pb_sheet_sync.responsible_team` に対する filter の
    // ホワイトリストなので、**過去チケットが持つ旧チーム名も残す**（消すと旧案件が
    // 絞り込めなくなる）。CRMT は 2026-08 に FCT へ統合されたが履歴のため残置。STT は
    // STYT の改名前の option 名で、改名前に同期された行が残るため同じく残置。
    // 改組前は FCT / CLT がそもそも欠けており、両チームの案件が絞り込めていなかった。
    teams: ['NBT', 'CDT', 'BDT', 'FCT', 'QDT', 'CLT', 'STYT', 'CRMT', 'STT'],
    teamColors: {
      CDT: { bg: '#BAE6FD', text: '#075985' }, // 淡い青
      BDT: { bg: '#FEF3C7', text: '#92400E' }, // 淡い黄
      CRMT: { bg: '#FCE7F3', text: '#9D174D' }, // 淡い桃（旧チーム。履歴用）
      QDT: { bg: '#D1FAE5', text: '#065F46' }, // 淡い緑
      NBT: { bg: '#EDE9FE', text: '#5B21B6' }, // 淡い紫
      FCT: { bg: '#FCE7F3', text: '#9D174D' }, // 淡い桃（旧 CRMT から継承）
      CLT: { bg: '#CCFBF1', text: '#115E59' }, // 淡い青緑
      STYT: { bg: '#E2E8F0', text: '#334155' }, // 淡い灰
      STT: { bg: '#E2E8F0', text: '#334155' }, // 淡い灰（STYT の旧 option 名。履歴用）
    },
  },
  mall_div: {
    divLabel: 'Mall Div',
    divBadgeColor: '#50BA52',
    businessTabs: null,
    teams: ['M-BDT', 'M-DMT', 'M-PST'],
    teamColors: {
      'M-BDT': { bg: '#DCFCE7', text: '#166534' }, // light
      'M-DMT': { bg: '#86EFAC', text: '#14532D' }, // mid
      'M-PST': { bg: '#22C55E', text: '#FFFFFF' }, // dark
    },
  },
  log_div: {
    // 注意: ここの divLabel 'Logi Div' は UI 表示用の社内呼称。BQ `pb_sheet_sync.division` 側は
    // 'Log Div'（短縮形）で別物。core-types `CROSS_DIV_PMO_SCOPE.log_div.divisionLabel` を BQ
    // クエリの WHERE 等価マッチに使い、こちらは画面の Div バッジに使う。両者を統一する場合は
    // 両ファイル + 既存 BQ データ（pb_sheet_sync.division）の整合性を必ず確認すること。
    divLabel: 'Logi Div',
    divBadgeColor: '#56C3DA',
    businessTabs: null,
    teams: ['LPT', 'SLT'],
    teamColors: {
      LPT: { bg: '#BAE6FD', text: '#075985' }, // light blue
      SLT: { bg: '#38BDF8', text: '#FFFFFF' }, // dark blue
    },
  },
} as const;
